import { useState } from 'react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { Input } from 'antd';
import {
  UPSERT_MULTIPLE_PROCESS_DEPENDENCY,
  UPDATE_PROCESSES_MUTATION,
} from '../queries';
import {
  PlusSquareOutlined,
  MinusSquareOutlined,
  EditOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, ModalClose, Spinner } from '../../../components';
import { useMutation } from '@apollo/client';
import {
  FindDependenciesByLocationWithoutChildren,
  ProcesessDependency,
  ProcessToUpdate,
} from './processesTypes';
import Loading from '../../../components/generic/Loading';
import Sucess from '../../../components/generic/sucess';

interface CommentsFormProps {
  onCancel: () => void;
  data: FindDependenciesByLocationWithoutChildren;
  isEditing: boolean;
  locationId?: string;
  processAreaId: string;
}

const CommentsForm = ({
  data,
  onCancel,
  isEditing = false,
  locationId,
  processAreaId,
}: CommentsFormProps) => {
  const [editingFields, seteditingFields] = useState(
    data.procesessDependencys.map((item) => false),
  );
  const { register, control, handleSubmit, getValues } = useForm({
    defaultValues: data,
  });
  const [sucess, setSucess] = useState(false);

  const mutation = isEditing
    ? UPSERT_MULTIPLE_PROCESS_DEPENDENCY
    : UPDATE_PROCESSES_MUTATION;
  const [updateProcesses, { loading, error }] = useMutation(mutation, {
    refetchQueries: ['FindDependenciesByLocation'],
  });
  const { TextArea } = Input;

  if (error) return <>Error! {error.message}</>;

  const enableEdit = (index: number) => {
    //update just the position of the editingFields arrays that matche the index
    seteditingFields(
      editingFields?.map((item: any, i: number) =>
        i === index ? !item : item,
      ),
    );
  };

  const findChangedProceeses = (
    previous: ProcesessDependency[],
    newareas: ProcessToUpdate[],
  ) => {
    const changedProcesses: ProcessToUpdate[] = [];

    newareas.forEach((newProcess) => {
      const matchingPrevProcess = previous.find(
        (prevProcess) => prevProcess.processUUID === newProcess.processUUID,
      );
      if (
        matchingPrevProcess &&
        matchingPrevProcess.percentage !== newProcess.percentage
      ) {
        changedProcesses.push({
          processUUID: newProcess.processUUID,
          percentage: newProcess.percentage,
          uuid: newProcess.uuid,
        });
      }
    });

    return changedProcesses;
  };

  const upsertProcesses = async (
    values: FindDependenciesByLocationWithoutChildren,
  ) => {
    const previousProcesses = data.procesessDependencys;
    const newProcesses = values.procesessDependencys.map(
      ({ percentage, processUUID, uuid }) => ({
        percentage: Number(percentage),
        processUUID,
        uuid,
      }),
    );
    const processes = findChangedProceeses(previousProcesses, newProcesses);

    if (processes.length === 0) {
      onCancel();
      return;
    }

    let ObjectToSend = {};
    const FinancialObject = {
      location: { uuid: locationId },
      processArea: { uuid: processAreaId },
      divisionFinancial: { uuid: values.key },
      procesess: processes,
    };
    const productGroupObject = {
      location: { uuid: locationId },
      processArea: { uuid: processAreaId },
      productGroup: { uuid: values.key },
      procesess: processes,
    };
    const productObject = {
      location: { uuid: locationId },
      processArea: { uuid: processAreaId },
      product: { uuid: values.key },
      procesess: processes,
    };
    const intermediateObject = {
      location: { uuid: locationId },
      processArea: { uuid: processAreaId },
      intermediate: { uuid: values.key },
      procesess: processes,
    };

    switch (values.__typename) {
      case 'DependencyMatrixModel':
        ObjectToSend = FinancialObject;
        break;
      case 'ProcessProductGroups':
        ObjectToSend = productGroupObject;
        break;
      case 'ProcessProducts':
        ObjectToSend = productObject;
        break;
      case 'ProcessIntermediates':
        ObjectToSend = intermediateObject;
        break;
    }
    try {
      await updateProcesses({ variables: { data: ObjectToSend } });
    } catch (err) {
      console.log(err);
    }
    onCancel();
  };

  const createComment = async (index: number) => {
    const values = getValues();
    const ObjectToSend = {
      processArea: { uuid: processAreaId },
      uuid: values.procesessDependencys[index].processUUID,
      comment: values.procesessDependencys[index].comment,
    };
    try {
      await updateProcesses({ variables: { data: ObjectToSend } });
    } catch (error) {
      console.log(error);
    }
    setSucess(true);
    setTimeout(() => {
      setSucess(false);
      enableEdit(index);
    }, 2000);
  };

  const onFinish: SubmitHandler<any> = (values: any) => {
    if (isEditing) {
      upsertProcesses(values);
    } else {
      onCancel();
    }
  };

  return (
    <form
      className="  w-full flex flex-col flex-nowrap mt-2"
      onSubmit={handleSubmit(onFinish)}
    >
      {isEditing && (
        <h1 className="  font-bold text-lg tracking-widest">{data?.name}</h1>
      )}
      <div className="inline-flex flex-col flex-nowrap items-center my-2 justify-between gap-3 max-h-130 overflow-y-scroll">
        {data?.procesessDependencys.map((item, index: any) => (
          <>
            {editingFields[index] && loading ? (
              <Loading />
            ) : sucess && editingFields[index] ? (
              <Sucess />
            ) : (
              <section className={`flex w-full justify-between items-start `}>
                <section className="flex items-center h-12 w-1/6">
                  <label
                    className="font-semibold text-primary-200 text-sm"
                    htmlFor={`${item.uuid}`}
                  >
                    {item.process}
                  </label>
                </section>
                {isEditing && (
                  <span className="mx-4 w-1/4 border rounded-lg text-right text-primary-200 font-normal text-sm px-2 py-1 h-auto">
                    <input
                      type="number"
                      step=".01"
                      min={0}
                      defaultValue={item.percentage}
                      className="w-24 bg-transparent border-none text-right text-primary-200 font-normal text-sm focus:outline-none focus:ring-0"
                      id={item.uuid!}
                      {...register(`procesessDependencys.${index}.percentage`, {
                        required: isEditing,
                        min: {
                          value: 0,
                          message: 'Percentage must be greater than 0.',
                        },
                      })}
                    />
                    %
                  </span>
                )}
                <section
                  className={`relative ${isEditing ? 'w-7/12' : 'w-3/4'} `}
                >
                  <Controller
                    control={control}
                    defaultValue={item.comment}
                    name={`procesessDependencys.${index}.comment`}
                    render={({ field }) => (
                      <TextArea
                        autoSize={{
                          minRows: !editingFields[index] ? 2 : 4,
                          maxRows: !editingFields[index] ? 2 : 6,
                        }}
                        readOnly={isEditing || !editingFields[index]}
                        className={`rounded-lg pr-8 w-full `}
                        id={`comment-${item.uuid}`}
                        {...field}
                      />
                    )}
                  />
                  <div
                    className={`bg-white absolute ${
                      item?.comment === '' ? '' : 'top-4 -translate-y-1/2'
                    } transform top-2 right-2`}
                  >
                    {!editingFields[index] ? (
                      !isEditing ? (
                        <EditOutlined
                          className="text-blue hover:cursor-pointer"
                          onClick={() => enableEdit(index)}
                        />
                      ) : (
                        <PlusSquareOutlined
                          className="text-blue hover:cursor-pointer"
                          onClick={() => enableEdit(index)}
                        />
                      )
                    ) : !isEditing ? (
                      <div
                        className={`flex items-center gap-2 ${
                          item?.comment !== ''
                            ? 'absolute -top-1.5 right-0'
                            : ''
                        }`}
                      >
                        <SaveOutlined
                          className="text-blue hover:cursor-pointer"
                          onClick={() =>
                            isEditing ? enableEdit(index) : createComment(index)
                          }
                        />
                        <ModalClose
                          className="w-3 h-3 fill-blue hover:cursor-pointer"
                          onClick={() => enableEdit(index)}
                        />
                      </div>
                    ) : (
                      <MinusSquareOutlined
                        className="text-blue hover:cursor-pointer"
                        onClick={() => enableEdit(index)}
                      />
                    )}
                  </div>
                </section>
              </section>
            )}
          </>
        ))}
      </div>
    </form>
  );
};

export default CommentsForm;
